import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import { useEffect, useState } from "react";
import ResponsiveTable, { Column } from "@/components/Usados/ResponsiveTable";
import ActionMenu from "@/components/Usados/ActionMenu";
import { Modal } from "@/components/Usados/modal";
import Button from "@/components/ui/button/Button";
import { useNavigate } from "react-router";
import Badge from "@/components/ui/badge/Badge";
import { Eye, Pencil, Plus, Trash } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Rol } from "@/types/roles";
import { apiFetch } from "@/service/api";

interface Sucursal {
  id: number;
  nombre: string;
  direccion: string;
  ciudad: string;
  pais: string;
  telefono: string;
  estado: boolean;
}

export default function ListarSucursales() {
  const { user } = useAuth();
  const rolId: number = user?.rol;
  const canDelete = rolId !== Rol.COLABORADOR && rolId !== Rol.ASISTENTE;
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sucursalToDelete, setSucursalToDelete] = useState<number | null>(null);
  const navigate = useNavigate();

  const fetchSucursales = async () => {
    try {
      const response = await apiFetch(`sucursales/find-all`);
      if (response.ok) {
        const data = await response.json();
        setSucursales(data);
      } else {
        const err = await response.text();
        console.error("Backend error:", err);
      }
    } catch (error) {
      console.error("Error fetching sucursales:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSucursales();
  }, []);

  const handleDelete = (id: number) => {
    setSucursalToDelete(id);
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!sucursalToDelete) return;
    try {
      const response = await apiFetch(
        `sucursales/remove/${sucursalToDelete}`,
        { method: "DELETE" },
      );
      if (response.ok) {
        setSucursales(sucursales.filter((s) => s.id !== sucursalToDelete));
        setIsModalOpen(false);
        setSucursalToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting sucursal:", error);
    }
  };

  const columns: Column<Sucursal>[] = [
    { label: "Nombre", key: "nombre" },
    { label: "Ciudad", key: "ciudad" },
    { label: "Telefono", key: "telefono" },
    {
      label: "Estado",
      key: "estado",
      render: (value: any) => (
        <Badge color={value ? "success" : "error"} variant="light">
          {value ? "Activo" : "Inactivo"}
        </Badge>
      ),
    },
    {
      label: "Acciones",
      key: "actions",
      render: (_, row) => (
        <ActionMenu
          actions={[
            {
              label: "Ver",
              icon: <Eye className="size-4" />,
              onClick: () => navigate(`/multiempresa/sucursales/ver/${row.id}`),
            },
            {
              label: "Editar",
              icon: <Pencil className="size-4" />,
              onClick: () =>
                navigate(`/multiempresa/sucursales/update/${row.id}`),
            },
            ...(canDelete
              ? [
                  {
                    label: "Eliminar",
                    icon: <Trash className="size-4" />,
                    onClick: () => handleDelete(row.id),
                    className: "text-red-500 hover:bg-red-50",
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <PageBreadcrumb pageTitle="Listado de Sucursales" />
      <div className="rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between mb-7">
          <h3 className="font-semibold text-gray-800 text-theme-xl dark:text-white/90">
            Sucursales Registradas
          </h3>
          <Button
            variant="primary"
            size="sm"
            startIcon={<Plus className="size-4" />}
            onClick={() => navigate("/multiempresa/sucursales/create")}
          >
            Nueva Sucursal
          </Button>
        </div>
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500"></div>
          </div>
        ) : (
          <ResponsiveTable columns={columns} data={sucursales} />
        )}
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="max-w- p-6"
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/10">
            <Trash className="size-8" />
          </div>
          <h3 className="mb-2 text-xl font-bold text-gray-800 dark:text-white/90">
            Confirmar Eliminacion
          </h3>
          <p className="mb-7 text-sm text-gray-500 dark:text-gray-400">
            ¿Estas seguro de que deseas eliminar esta sucursal? Esta accion no
            se puede deshacer.
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={confirmDelete}
              className="flex-1 bg-red-600 hover:bg-red-700!border-red-600"
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
